import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FileText, Phone, Gift, Star, Users, ArrowRight, Check } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const steps = [
  {
    number: "01",
    title: "The Handoff",
    icon: FileText,
    description: "Submit a closed loan via our secure portal. It takes just 30 seconds to provide us with the essential details we need to begin.",
    details: [
      "Borrower contact information",
      "Loan details & closing date",
      "Your branding preferences",
      "Gift budget (optional)"
    ],
    whatYouDo: "Spend 30 seconds submitting the file",
    whatWeDo: "Handle everything from here"
  },
  {
    number: "02",
    title: "The Introduction",
    icon: Phone,
    description: "Your dedicated concierge introduces themselves to your client as an extension of YOUR team, maintaining your brand presence.",
    details: [
      "Personalized email introduction",
      "Follow-up phone call within 48 hours",
      "Congratulatory message on their new home",
      "Introduction to the concierge experience"
    ],
    whatYouDo: "Nothing - we've got this",
    whatWeDo: "Make the first contact on your behalf"
  },
  {
    number: "03",
    title: "The Experience",
    icon: Gift,
    description: "We coordinate thoughtful gifts, send thank you notes, and perform check-in calls to ensure your clients feel valued and appreciated.",
    details: [
      "Gift selection based on client preferences",
      "Personalized thank you notes",
      "30-day check-in calls",
      "Address any post-closing questions"
    ],
    whatYouDo: "Enjoy the positive feedback",
    whatWeDo: "Delight your clients with white-glove service"
  },
  {
    number: "04",
    title: "The Harvest",
    icon: Star,
    description: "We professionally request reviews on your preferred platforms and ask for referrals, all while maintaining a natural, non-pushy approach.",
    details: [
      "Review requests on Google, Zillow, etc.",
      "Timing optimized for best response",
      "Referral conversations with interested clients",
      "Follow-up sequences for maximum conversion"
    ],
    whatYouDo: "Watch your reviews grow",
    whatWeDo: "Generate reviews & capture referrals"
  },
  {
    number: "05",
    title: "The Delivery",
    icon: Users,
    description: "Referrals are captured and routed directly back to you with all relevant information, ready for you to convert into your next closing.",
    details: [
      "Referral details delivered to you",
      "Contact information & context provided",
      "Warm introduction available if requested",
      "Monthly reporting on all activities"
    ],
    whatYouDo: "Close more loans",
    whatWeDo: "Keep the referral engine running"
  }
];

const timeline = [
  { day: "Day 1-2", activity: "Initial outreach & introduction" },
  { day: "Day 3-7", activity: "Gift coordination & delivery" },
  { day: "Day 7-14", activity: "Review request sequence" },
  { day: "Day 14-21", activity: "Referral conversations" },
  { day: "Day 21-30", activity: "Check-in & final follow-up" }
];

export const HowItWorksPage = () => {
  return (
    <div data-testid="how-it-works-page" className="min-h-screen bg-[#0F0F0F]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="h-[2px] w-16 bg-[#C9A227] mx-auto mb-8" />
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Our Process</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              How Better Closings <span className="text-[#E5D5A5]">Works</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              A seamless, five-step process designed to maximize client satisfaction, 
              generate reviews, and capture referrals—all without adding work to your plate.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section data-testid="steps-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-24">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`step-${step.number}`}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content Side */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-[#C9A227] font-serif text-5xl font-bold">{step.number}</span>
                    <div className="h-px flex-1 bg-[#C9A227]/30" />
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-4">
                    {step.title}
                  </h2>
                  <p className="text-neutral-300 text-lg leading-relaxed mb-8">
                    {step.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {step.details.map((detail) => (
                      <li key={detail} className="flex items-start text-neutral-400">
                        <Check className="w-5 h-5 text-[#C9A227] mr-3 mt-0.5 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visual Side */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-[#1A1A1A] border border-white/5 p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A227]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <step.icon className="w-16 h-16 text-[#C9A227] mb-8" strokeWidth={1} />
                    
                    <div className="space-y-6">
                      <div className="bg-[#141414] border border-white/5 p-4">
                        <p className="text-xs text-[#C9A227] uppercase tracking-wider mb-2">What You Do</p>
                        <p className="text-white">{step.whatYouDo}</p>
                      </div>
                      <div className="bg-[#141414] border border-[#C9A227]/30 p-4">
                        <p className="text-xs text-[#C9A227] uppercase tracking-wider mb-2">What We Do</p>
                        <p className="text-white">{step.whatWeDo}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section data-testid="timeline-section" className="py-24 bg-[#141414] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">30-Day Timeline</p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white">
              What to <span className="text-[#E5D5A5]">Expect</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-6">
            {timeline.map((item, index) => (
              <motion.div
                key={item.day}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative text-center"
              >
                <div className="bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#C9A227]/30 transition-all duration-300">
                  <p className="text-[#C9A227] font-serif text-lg font-semibold mb-2">{item.day}</p>
                  <p className="text-neutral-400 text-sm">{item.activity}</p>
                </div>
                {index < timeline.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-3 w-6 h-6 text-[#C9A227]/50 -translate-y-1/2" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to Get <span className="text-[#E5D5A5]">Started?</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              See how Better Closings can transform your post-close experience 
              and start generating more reviews and referrals today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="how-it-works-cta-primary">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-10 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  Book a Demo
                </Button>
              </Link>
              <Link to="/pricing" data-testid="how-it-works-cta-secondary">
                <Button variant="outline" className="bg-transparent border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 rounded-none px-10 py-6 font-semibold text-sm tracking-wider uppercase transition-all duration-300">
                  View Pricing
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
